import type {ClientStatusPublic} from '@wix/cashier-common/dist/src/types/PaymentStatus'
import {poll, waitALittle} from '@wix/panda-js-utils'
import {
  EVENTS_APP_ID,
  EVENTS_DETAILS_PAGE_ID,
  PlanInfo,
  Submissions,
  getCouponDiscount,
  getCouponDiscountCode,
  getOrderGiftCardsTotalAmount,
  getValidGiftCardDetails,
} from '@wix/wix-events-commons-statics'
import {getLocale} from '../../../../commons/selectors/environment'
import {isMembersInstalled} from '../../../../commons/selectors/installed-apps'
import {getBuyerDetails, getTicketsDetails} from '../selectors/checkout'
import {getCurrentMemberDetails} from '../selectors/current-member-details'
import {getEvent} from '../selectors/event'
import {getCouponCode, getGiftCardCode} from '../selectors/invoice'
import {getNavigationQuery, getPaymentRedirectUrl} from '../selectors/navigation'
import {getSelectedPaidPlan} from '../selectors/paid-plans'
import {isRequestPending} from '../selectors/pending-requests'
import {getInvoice, isPreparingOrder} from '../selectors/placed-order'
import {getReservationId} from '../selectors/reservation'
import {Interaction} from '../services/logger'
import {createAsyncAction} from '../services/redux-toolkit'
import {RegFormData} from '../types/registration'
import {GetState} from '../types/state'
import {StoreExtraArgs} from '../types/store-extra-arg'
import {extractFormData} from '../utils/api-data-mapper'
import {applyCoupon} from './coupon'
import {applyGiftCard} from './gift-card'
import {navigateToLinkExpired, navigateToNotFound, navigateToOrderUrl} from './navigation'
import {applyPlan} from './paid-plans'

export const placeOrder = createAsyncAction<
  {order: wix.events.ticketing.Order},
  {
    eventId: string
    buyer?: RegFormData
    guests?: RegFormData[]
    buyerSubmission?: Submissions
    guestSubmissions?: Submissions[]
  }
>(
  'PLACE_ORDER',
  async (
    {eventId, buyer, guests, buyerSubmission, guestSubmissions},
    {getState, extra: {wixCodeApi, logger, serverApi}},
  ) => {
    const state = getState()

    logger.startInteraction(Interaction.Checkout)

    const couponCode = getCouponDiscountCode(getInvoice(state))
    const giftCardCode = getGiftCardCode(state)
    const memberData = getCurrentMemberDetails(state)
    const memberId = isMembersInstalled(state) && memberData ? memberData.id : undefined
    const selectedPlan = getSelectedPaidPlan(state) || ({} as PlanInfo)
    const {benefitId, planOrderId} = selectedPlan
    const paymentRedirectUrl = getPaymentRedirectUrl(state, wixCodeApi)
    const reservationId = getReservationId(state)

    const orderGuests = guests ? guests.map(extractFormData) : null

    const response = await serverApi.placeOrder({
      eventId,
      buyer,
      guests: orderGuests as any,
      reservationId,
      buyerSubmission,
      guestSubmissions,
      couponCode,
      giftCardCode,
      memberId,
      planOrderId,
      benefitId,
      locale: getLocale(state),
      paymentRedirectUrl,
    })

    return response
  },
  {
    condition: (_, {getState}) => {
      const state = getState()
      return !isRequestPending(state, placeOrder.typePrefix)
    },
  },
)

export const postPlaceOrder =
  (order: wix.events.ticketing.Order) =>
  (dispatch: Function, getState: GetState, {logger}: StoreExtraArgs) => {
    logger.finishInteraction(Interaction.Checkout)
    const totalPrice = Number(order.totalPrice.amount) - getOrderGiftCardsTotalAmount(order.giftCardPaymentDetails)

    if (!totalPrice) {
      dispatch(navigateToOrderUrl(getState().placedOrder.orderPageUrl))
    }
  }

interface UpdateOrderParams {
  buyer?: RegFormData
  guests?: RegFormData[]
  buyerSubmission?: Submissions
  guestSubmissions?: Submissions[]
  paymentDetailsId?: string
  paymentMethod?: string
}

export const updatePaymentMethod =
  ({paymentMethod}: {paymentMethod: string}) =>
  (dispatch: Function, getState: GetState) => {
    const state = getState()
    const buyer = state.checkout.buyerDetailsOld
    const guests = state.checkout.ticketsDetailsOld
    const buyerSubmission = getBuyerDetails(state)
    const guestSubmissions = getTicketsDetails(state)

    return dispatch(updateOrder({buyer, guests, buyerSubmission, guestSubmissions, paymentMethod})).unwrap()
  }

export const updateOrder = createAsyncAction<wix.events.ticketing.UpdateCheckoutResponse, UpdateOrderParams>(
  'UPDATE_ORDER',
  async (
    {buyer, guests, buyerSubmission, guestSubmissions, paymentDetailsId, paymentMethod},
    {getState, extra: {serverApi}},
  ) => {
    const state = getState()
    const eventId = getEvent(state).id
    const selectedPlan = getSelectedPaidPlan(state) || ({} as PlanInfo)
    const {benefitId, planOrderId} = selectedPlan

    const response = await serverApi.updateOrder({
      eventId,
      paymentDetailsId,
      paymentMethod,
      orderNumber: state.placedOrder.order.orderNumber,
      ...(buyerSubmission
        ? {
            buyerSubmission,
            guestSubmissions,
          }
        : {
            buyer: extractFormData(buyer) as unknown as RegFormData,
            guests: guests?.map(extractFormData) as unknown as RegFormData[],
          }),
      locale: getLocale(state),
      couponCode: getCouponCode(state),
      giftCardCode: getGiftCardCode(state),
      paidPlanBenefit: benefitId && planOrderId ? {benefitId, planOrderId} : undefined,
    })

    return response
  },
)

export const expressCheckout = createAsyncAction<{clientStatus: ClientStatusPublic}, {paymentDetailsId: string}>(
  'EXPRESS_CHECKOUT',
  async (params, {dispatch, extra: {flowAPI}, getState}) => {
    const state = getState()
    const buyerSubmission = getBuyerDetails(state)
    const guestSubmissions = getTicketsDetails(state)

    const [{confirmPayment}, updateOrderResponse] = await Promise.all([
      import('@wix/cashier-express-checkout-widget/dist/src/utils/confirmPayment'),
      dispatch(
        updateOrder({
          buyer: state.checkout.buyerDetailsOld,
          guests: state.checkout.ticketsDetailsOld,
          buyerSubmission,
          guestSubmissions,
          paymentDetailsId: params.paymentDetailsId,
        }),
      ).unwrap(),
    ])

    const {chargeToken, orderPageUrls} = updateOrderResponse

    const confirmPaymentResponse = await confirmPayment({
      meta: {
        appDefId: EVENTS_APP_ID,
        msid: flowAPI.controllerConfig.platformAPIs.bi?.metaSiteId,
        visitorId: flowAPI.controllerConfig.platformAPIs?.bi?.visitorId,
        widgetId: EVENTS_DETAILS_PAGE_ID,
      },
      biLoggerFactory: flowAPI.essentials.biLoggerFactory,
      chargeResponseToken: chargeToken,
      redirectFn: flowAPI.controllerConfig.wixCodeApi.location.to,
      newApi: true,
    })

    switch (confirmPaymentResponse.clientStatus) {
      case 'Approved':
        dispatch(navigateToOrderUrl(orderPageUrls.success))
        break
      case 'Pending':
      case 'InProcess':
        dispatch(navigateToOrderUrl(orderPageUrls.pending))
        break
      case 'Failed':
      case 'Unknown':
      default:
        dispatch(navigateToOrderUrl(orderPageUrls.error))
        break
    }

    return confirmPaymentResponse
  },
)

export const getCheckoutData = createAsyncAction<void, {eventId: string; reservationId: string; orderNumber?: string}>(
  'GET_CHECKOUT_DATA',
  async ({eventId, reservationId, orderNumber}, {dispatch, getState}) => {
    const {order} = orderNumber ? await dispatch(getOrder({eventId, orderNumber})).unwrap() : {order: undefined}
    const selectedPaidPlan = getSelectedPaidPlan(getState())

    if (order) {
      const validGiftCardDetails = getValidGiftCardDetails(order.giftCardPaymentDetails)

      if (validGiftCardDetails.length) {
        dispatch(applyGiftCard(eventId, order.reservationId, validGiftCardDetails[0].code))
      }

      if (getCouponDiscount(order.invoice)) {
        dispatch(applyCoupon(eventId, order.reservationId, getCouponDiscountCode(order.invoice)))
      }
    }

    if (selectedPaidPlan) {
      dispatch(applyPlan({eventId, reservationId, planOrderId: selectedPaidPlan.planOrderId}))
    }
  },
)

export const getOrder = createAsyncAction<
  {order: wix.events.ticketing.Order; calendarLinks: any; dates: any},
  {eventId: string; orderNumber: string; token?: string}
>('GET_ORDER', async ({eventId, orderNumber, token = ''}, {extra: {serverApi, flowAPI}, dispatch}) => {
  try {
    if (token) {
      const response = await serverApi.getOrderByToken(token)
      return response
    }

    if (eventId && orderNumber) {
      const response = await serverApi.getOrder(eventId, orderNumber)
      return response
    }

    throw new Error(`Missing params to get order, eventId: ${eventId} orderNumber: ${orderNumber} token: ${token}`)
  } catch (error) {
    flowAPI.panoramaClient.errorMonitor().reportError(error)
    const responseStatus = error.response?.status

    if (responseStatus === 404) {
      dispatch(navigateToNotFound())
    } else if (responseStatus === 400) {
      dispatch(navigateToLinkExpired())
    } else {
      dispatch(navigateToNotFound())
    }
  }
})

export const pollOrder = createAsyncAction<wix.events.ticketing.Order>(
  'POLL_ORDER',
  async (_, {extra: {serverApi}, getState}) => {
    const state = getState()
    const {token} = state.navigation?.query ?? {}

    let response
    await poll({
      callback: async () => {
        response = await serverApi.getOrderByToken(token)

        return !isPreparingOrder(response.order)
      },
      intervalMs: 2000,
      totalMs: 15000,
    })
    return response.order
  },
)

export const getOrderTicketsDownloadUrl = createAsyncAction<
  {loadingTicketsUrl: boolean; downloadTicketsUrl?: string},
  number
>('GET_ORDER_TICKETS_DOWNLOAD_URL', async (retryLimit, {dispatch, getState, extra: {serverApi, logger}}) => {
  const {token} = getNavigationQuery(getState())

  if (!token) {
    return {loadingTicketsUrl: false}
  }

  try {
    const response = await serverApi.getPapyrusTicketsDownloadUrl({token})
    if (response.ready && response.downloadUrl) {
      logger.finishInteraction(Interaction.LoadingTickets)
      return {loadingTicketsUrl: !response.ready, downloadTicketsUrl: response.downloadUrl}
    }
    if (retryLimit > 0) {
      await waitALittle()
      dispatch(getOrderTicketsDownloadUrl(--retryLimit))
      return {loadingTicketsUrl: true}
    }
    throw new Error(`Automatic retry limit exceeded, please retry by clicking download`)
  } catch {
    return {loadingTicketsUrl: false}
  }
})
